<template>
  <AppLoader v-if="isLoading" />
  <section v-else-if="posts.length" class="blog-preview">
    <h2 class="blog-preview__title h4">
      {{ $t("blog.preview.title") }}
    </h2>

    <div class="blog-preview__container">
      <div class="blog-preview__columns">
        <div
          v-for="(column, index) of columns"
          :key="index"
          class="blog-preview__column"
        >
          <article
            v-for="post of column"
            :key="post.id"
            class="blog-preview__post"
          >
            <a
              class="blog-preview__post-wrapper"
              :href="post.postUrl"
              rel="noopener noreferrer"
              target="_blank"
            >
              <div class="blog-preview__post-content">
                <div v-if="post.imageUrl" class="blog-preview__post-image">
                  <img :src="post.imageUrl" :alt="post.title" />
                </div>

                <h3 class="blog-preview__post-title h4">
                  {{ post.title }}
                </h3>

                <LimitedContent :line-count="3">
                  <p style="white-space: pre-line">
                    {{ post.excerpt }}
                  </p>
                </LimitedContent>

                <div class="blog-preview__post-category">
                  {{ post.categoryTitle }} • {{ formatDate(post.publishedAt) }}
                </div>
              </div>
            </a>
          </article>
        </div>
      </div>

      <div class="blog-preview__actions">
        <a :href="url" rel="noopener noreferrer" target="_blank" class="btn">
          {{ $t("blog.preview.link") }}
        </a>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, onMounted, computed } from "vue";
import { api } from "../../api";
import { BlogPost } from "../../types/blog-post.interface";
import LimitedContent from "../../components/limited-content.vue";
import { formatDate } from "../../utils/format-date";
import AppLoader from "@/components/app-loader.vue";

const posts = ref<BlogPost[]>([]);
const url = "https://mangumangas.lt/blog/";
const isLoading = ref(false);

const columns = computed(() => {
  if (!posts.value) {
    return [];
  }

  return [
    posts.value.filter((_, index) => index % 3 === 0),
    posts.value.filter((_, index) => index % 3 === 1),
    posts.value.filter((_, index) => index % 3 === 2),
  ];
});

const loadImages = (posts: BlogPost[]) => {
  return Promise.all(
    posts.map(async (post) => {
      if (post.imageUrl) {
        try {
          await loadImage(post.imageUrl);
        } catch {
          post.imageUrl = "";
        }
      }
    })
  );
};

const loadImage = (src: string) => {
  return new Promise((resolve, reject) => {
    const img = new Image();
    img.src = src;
    img.onload = () => resolve(true);
    img.onerror = (err) => reject(err);
  });
};

onMounted(async () => {
  isLoading.value = true;

  try {
    const blog = await api.blog.getPosts();
    await loadImages(blog);
    posts.value = blog;
  } finally {
    isLoading.value = false;
  }
});
</script>
